/**
 * @typedef {import('App/Admin/Groups/EditGroupTheme/Props').default} Props
 * @typedef {import('shared/ThemeService/Theme').default} Theme
 * @typedef {import('shared/ThemeService/ThemeColors').default} ThemeColors
 */

import { themeService } from 'service-container';
import { translate } from 'App/Internationalization';
import EditTheme from 'App/shared/EditTheme';
import flags from '../../../../shared/Flags';
import Loading from 'shared/Loading';
import NoContent from '../../../../shared/NoContent';
import React from 'react';
import useGetGroup from '../../../../Hooks/Admin/Getters/useGetGroup/useGetGroup';
import useIsGated from '../../../../Hooks/Shared/useIsGated/useIsGated';
import paths from '../../../../config/local/paths';

const defaultGroupName = translate('global.group');

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
export function EditGroupTheme(props) {
	const groupId = props.match.params.groupId;
	const gated = useIsGated(flags.branding.colorLogos, { groupId });
	const group = useGetGroup(groupId, gated.isGated);

	/** @returns {Promise<Theme>} */
	function getTheme() {
		return themeService.getGroupTheme(groupId);
	}

	/**
	 * @param {File} favicon
	 * @returns {Promise<void>}
	 */
	function setFavicon(favicon) {
		return themeService.setGroupFavicon(groupId, favicon);
	}

	/**
	 * @param {File} logo
	 * @returns {Promise<void>}
	 */
	function setLogo(logo) {
		return themeService.setGroupLogo(groupId, logo);
	}

	/**
	 * @param {ThemeColors} colors
	 * @returns {Promise<void>}
	 */
	function setTheme(colors) {
		return themeService.setGroupTheme(groupId, colors);
	}

	if (gated.isGettingData || group.isGettingData) {
		return <Loading />;
	}

	if (group.isError) {
		return (
			<NoContent
				message={translate('admin.pages.import_agents.group_not_found')}
			/>
		);
	}

	return (
		<EditTheme
			getTheme={getTheme}
			heading={translate(
				'global.header.edit_branding',
				group.data?.name || defaultGroupName
			)}
			isGated={gated.isGated}
			isGettingData={gated.isGettingData}
			setFavicon={setFavicon}
			setLogo={setLogo}
			setTheme={setTheme}
			subscriptionLink={`${paths.app.admin.groups.root}/${groupId}/subscription`}
			userType="administrator"
		/>
	);
}

export default React.memo(EditGroupTheme);
